import { Page } from "../enums/Page";
import { css } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { GameState } from "@shared/types";
import { CharacterProfileService } from "../services/CharacterProfileService";

const styles: string = css`
       .bg {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            z-index: -1;
       }
       
       :host {
            font-family: "Onesize", serif;
            color: #2f1e0e;
            width: 100%;
            max-width: 1024px;
            height: 100%;
            display: flex;
            flex-direction: column;
            padding: 1rem;
            box-sizing: border-box;
        }

       .title {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 2rem;
            font-weight: bold;
            text-shadow: 1px 1px 2px #fff8e1;
        }

        .header {
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            margin-top: 10px;
        }

       .header img:not([src=""]) {
            border: 5px double #6b4c2e;
            border-radius: 8px;
            padding: 5px;
            background-color: #d9c2a3;
            width: 300px;
            height: auto;
            image-rendering: pixelated;
        }

       .header img:nth-child(n + 2) {
            position: absolute;
        }

       .content {
            flex-grow: 1;
            overflow: auto;
            margin-top: 10px;
            padding: 0 10px;
        }

       .content p {
            margin: 0 0 10px 0;
        }

       .content p:last-of-type {
            margin: 0;
        }

       .footer {
            border-radius: 10px 10px 0 0;
            border: 2px solid #7c5a34;
            margin-top: 10px;
            display: flex;
        }

       .footer .buttons {
            display: flex;
            flex-direction: row;
            padding: 10px 10px 0 10px;
        }

       .footer .button {
            font-family: "Onesize";
            font-size: 15px;
            border-radius: 5px;
            color: #ffffff;
            background-color: #d9c2a3;
            border: 1px solid #6b4c2e;
            padding: 5px 10px;
            margin: 0 0 8px 8px;
            text-transform: uppercase;
            color: #2f1e0e;
            cursor: pointer;
            display: inline-block;
            user-select: none;
        }

       .footer .button:hover {
            background-color: #c4a97c;
        }
`;

/**
 * Represents the Character Profile Web Component
 */
export class CharacterProfileComponent extends HTMLElement {
    /** Instance of the game event service */
    private _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private _gameRouteService: GameRouteService = new GameRouteService();
    /** Instance of the character profile service */
    private _characterProfileService: CharacterProfileService = new CharacterProfileService();

    private selectedCharacter: string | null = null;

    /**
     * The "constructor" of a Web Component
     */
    public async connectedCallback(): Promise<void> {
        this.attachShadow({ mode: "open" });
        await this.render();

        void this.refreshGameState();
        this.shadowRoot?.addEventListener("click", event => {
            if ((event.target as HTMLButtonElement).id === "closeButton") {
                this._gameEventService.switchPage(Page.Canvas);
            }
        });
    }

    /**
          * Refresh the current game state
          */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
          * Update the canvas to the provided game state
          *
          * @param state Game state to update the canvas to
          */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }
    }

    /**
     * Fetches the list of available characters.
     *
     * @returns {Promise<{ alias: string; name: string }[]>} A promise that resolves to an array of character objects with aliases and names.
     */
    private async fetchCharacterList(): Promise<{ alias: string; name: string }[]> {
        return await this._characterProfileService.getCharacterList();
    }

    /**
     * Fetches the basic profile information (title, description, image) for a specific character.
     *
     * @param alias The alias of the character whose profile is to be fetched.
     * @returns {Promise<{ title: string; description: string; image: string }>} A promise that resolves to the character's profile.
     */
    private async fetchCharacterProfile(alias: string): Promise<{ title: string; description: string; image: string }> {
        return await this._characterProfileService.getCharacterProfile(alias);
    }

    /**
     * Fetches detailed information (appearance, motivation) for a specific character.
     *
     * @param alias The alias of the character whose detailed profile is to be fetched.
     * @returns {Promise<{ title: string; description: string; appearance?: string; motivation?: string }>} A promise that resolves to the character's detailed profile.
     */
    private async fetchDetailedCharacterProfile(alias: string): Promise<{ title: string; description: string; appearance?: string; motivation?: string }> {
        return await this._characterProfileService.getDetailedCharacterProfile(alias);
    }

    /**
     * Renders the component's content, including the character profile and associated information.
     */
    private async render(): Promise<void> {
        if (!this.shadowRoot) return;

        let title: string = "Character Profiles";
        let content: string = `
                <p>Welcome or welcome back to the character profiles, where your dicoveries about the characters are saved in on of these profiles.</p>
                <p>I see you would like to know more about the characters or perhaps you have forgotten a thing or two.</p>
                <p>Choose any of the characters you're interested in and you will see a bit about them. And remember the more you find out,
                the more information there will be in the profile for you to see.</p>
        `;
        let image: string = "";

        if (this.selectedCharacter) {
            const basicProfile: { title: string; description: string; image: string } = await this.fetchCharacterProfile(this.selectedCharacter);
            title = basicProfile.title;
            content = basicProfile.description;
            image = basicProfile.image;

            const extraDetails: { title: string; description: string; appearance?: string; motivation?: string } = await this.fetchDetailedCharacterProfile(this.selectedCharacter);

            if (extraDetails.appearance || extraDetails.motivation) {
                content += `<p>Appearance: ${extraDetails.appearance ?? "???"}</p>`;
                content += `<p>Motivation: ${extraDetails.motivation ?? "???"}</p>`;
            }
        }

        this.shadowRoot.innerHTML = `
            <style>${styles}</style>
            ${this.backgroundImage()}
            <div class="title">${title}</div>
            <div class="header"><img src="${image}" class="character-image"></div>
            <div class="content">${content}</div>
            <div class="footer"><div class="buttons"></div></div>
        `;

        const characters: ({ alias: string; name: string } []) = await this.fetchCharacterList();
        this.renderButtons(characters);
    }

    /**
     * Generates the background image for the component, depending on the selected character.
     *
     * @returns {string} The HTML string for the background element.
     */
    private backgroundImage(): string {
        let background: string;
        if (!this.selectedCharacter) {
            background = "<img src='/assets/img/rooms/background.png' class='bg'>";
        }
        else {
            background = "<div><img src='/assets/img/rooms/background.png' class='bg'><div>";
        }

        return background;
    }

    /**
     * Renders the buttons for each character, allowing the user to select a character to view its profile.
     *
     * @param characters The list of available characters to display as buttons.
     */
    private renderButtons(characters: { alias: string; name: string }[]): void {
        const buttonsContainer: HTMLButtonElement | null | undefined = this.shadowRoot?.querySelector(".buttons");
        console.log(buttonsContainer);
        if (!buttonsContainer) return;

        buttonsContainer.innerHTML = "";

        characters.forEach(character => {
            const button: HTMLButtonElement = document.createElement("button");
            button.classList.add("button");
            button.setAttribute("data-char", character.alias);
            button.textContent = character.name;

            button.addEventListener("click", async () => {
                this.selectedCharacter = character.alias;
                await this.render();
            });

            buttonsContainer.appendChild(button);
        });

        const closeButton: HTMLButtonElement = document.createElement("button");
        closeButton.id = "closeButton";
        closeButton.classList.add("button");
        closeButton.textContent = "X";

        buttonsContainer.appendChild(closeButton);
    }
}
